import { ISettings } from '@/models/Auth/AuthSessionModel';
import { IHTTPParams, IRequestWrap } from '@/server/IServer';
import { TValidateReturn, validate as excuteValidation } from '@/services/JSCService';
import { IAxiosRequesterConfig, THTTPMethods } from '@/services/RequestService/requesters/AxiosRequester';
import { checkIsBrowser } from '@/utils/functions/checkServerEnvironment';
import { AxiosRequestConfig } from 'axios';
import md5 from 'md5';
import { defaultTo, mergeWith } from 'ramda';
import URI from 'urijs';
import UUID from 'uuid';

export interface ISunnyRequestBuilderParams {
  /** 인증키, 서버사이드. */
  accessToken?: string;
  /** API 엔드포인트 위치. 지정하지 않으면 Sunny 경로. (`//nol.yanolja.com/...`) */
  baseURI?: URI;
  /** Cognito Identity ID, 서버사이드 `req`. */
  cgntId?: string;
  clientIP?: string;
  /** 모바일/비-모바일, 서버사이드 `req`. */
  platform?: 'Web' | 'MWeb';
  /** 사용자 에이전트, 서버사이드 `req`. */
  userAgent?: string;
  ywt?: string;
  /** 테스트 숙소 노출여부 */
  testProduct?: ISettings['testProduct'];
  /** 요청 추적을 위한 값 (https://yanolja.atlassian.net/wiki/spaces/platformdiv/pages/89671996) */
  traceID?: string;
  /**
   * Joy, Display 실험군 정보
   * @see https://yanolja.atlassian.net/wiki/spaces/CXT/pages/134792596
   */
  expGroups?: string;
  /** 쿠키 */
  cookie?: string;
}

export interface ISunnyRequestBuilderConstructor<T extends SunnyRequestBuilder> {
  new (params?: ISunnyRequestBuilderParams): T;
  /** @static */
  fromReq(req: IRequestWrap): T;
  /** @static */
  getBaseURIFromReq(req: IRequestWrap): URI;
}

/**
 * Action으로 요청을 하기 전에 Query클래스를 사용하여 어떤 URL로 어떤 패러매터를 전달할지 만들게 되는데
 * Query 클래스를 만들 때 반드시 이 클래스를 상속하고 super를 호출하여야 한다
 * 최초 객체 생성 이후 Query 클래스의 각 메서드는 getAPIPath 메서드로 만들어진 주소를 가져다가 쓴다
 */
export abstract class SunnyRequestBuilder {
  protected baseURI: URI;
  protected cgntId: string | undefined;
  protected clientIP: string | undefined;
  /** @deprecated `baseURI`를 지정하지 말 것 */
  protected isProxy: boolean = false; // [todo] 지금 지우면 변경점이 너무 많음
  protected platform: 'Web' | 'MWeb' | undefined;
  protected userAgent: string | undefined;
  protected ywt: string | undefined;
  protected testProduct: ISettings['testProduct'] | undefined;

  private accessToken: string | undefined;
  private traceID: string | undefined;
  private expGroups: string | undefined;
  private cookie: string | undefined;

  constructor({
    accessToken,
    baseURI = URI(''),
    cgntId,
    clientIP,
    platform,
    userAgent,
    ywt,
    testProduct,
    traceID,
    expGroups,
    cookie,
  }: ISunnyRequestBuilderParams = {}) {
    this.accessToken = accessToken;
    this.baseURI = baseURI;
    this.cgntId = cgntId;
    this.clientIP = clientIP;
    this.platform = platform;
    this.userAgent = userAgent;
    this.ywt = ywt;
    this.testProduct = testProduct;
    this.traceID = traceID;
    this.expGroups = expGroups;
    this.cookie = cookie;
  }

  get AccessTokenObject(): { 'access-token'?: string } {
    return this.accessToken ? { 'access-token': this.accessToken } : {};
  }

  /**
   * 공용 헤더를 담은 객체를 반환한다.
   */
  get CommonHeaders(): Record<string, string | undefined> {
    // 없는 값은 #makeHeaders()에서 지워질 것이므로 막 넣음
    const commonHeader = {
      'access-token': this.accessToken,
      'client-ip': this.clientIP,
      cognitoId: this.cgntId,
      platform: this.platform,
      'user-agent': this.userAgent,
      uuid: this.cgntId && md5(this.cgntId),
      ywt: this.ywt,
      'Test-Product': this.testProduct,
      'X-Trace-Id': this.traceID,
      'Exp-Groups': this.expGroups,
      cookie: this.cookie,
    };
    return commonHeader;
  }

  /**
   * 서버사이드 리퀘스트 빌더 인스턴스를 생성한다. 지정한 `req` 객체에서 토큰, Cognito ID, 클라이언트 IP 등 필요한 정보를 자동으로 가져온다.
   */
  static fromReq<T extends SunnyRequestBuilder>(this: ISunnyRequestBuilderConstructor<T>, req: IRequestWrap): T {
    if (!req || !req.config) {
      return new this();
    }

    const device = req.ua.getDevice();
    return new this({
      accessToken: req.config.Token,
      baseURI: this.getBaseURIFromReq(req),
      cgntId: req.cookies['cgntId'] || req.sunnyMeta['cgntId'],
      clientIP: (req.headers['x-forwarded-for'] as string) || req.connection.remoteAddress,
      platform: device.type === 'mobile' || device.type === 'tablet' ? 'MWeb' : 'Web',
      userAgent: req.ua.getUA(),
      ywt: req.session.getYWT(),
      testProduct: req.session.getSettings?.().testProduct,
      traceID: (req.headers['X-Trace-Id'] as string) || UUID.v4(),
      expGroups: req.serviceConfig?.serverExpGroups ?? '',
      cookie: req.headers.cookie,
    });
  }

  /**
   * `.fromReq()`에서 어떤 `baseURI`를 사용할지 결정한다.
   * @example
   * class EventRequestBuilder extends SunnyRequestBuilder {
   *   protected static getBaseURIFromReq(req: IRequestWrap) { return req.config.OpenAPI }
   * }
   */
  static getBaseURIFromReq<T extends SunnyRequestBuilder>(
    this: ISunnyRequestBuilderConstructor<T>,
    req: IRequestWrap,
  ): URI {
    return req.config.Joy;
  }

  /**
   * 요청에 사용할 헤더 객체를 생성한다.
   */
  protected makeHeaders(headers?: Record<string, string | undefined>): Record<string, string | undefined> | undefined {
    // params.headers에 user-agent가 섞여있어도 CommonHeaders로 덮어 씌움
    // 단, CommonHeaders 중 값이 undefined인 키는 무시함 (= mergeWith defaultTo)
    const resultHeaders = mergeWith(defaultTo, headers || {}, this.CommonHeaders);
    if (headers?.['uuid']) {
      // 회원통합 이후 nol-device-id로 전달할 값. 쿠키(nol_device_id) 혹은 헤더(nol-device-id)로 전달된다.
      resultHeaders.uuid = headers['uuid'];
    }
    return resultHeaders;
  }

  /**
   * baseURI가 정의되어 있지 않으면 Sunny 앱 서버에 요청하는 prefix를 붙인다.
   * @deprecated `#getAPIEndpoint()`
   */
  protected getAPIPath(path: string): URI {
    const href = this.baseURI.clone();
    return href.equals('') ? href.path(`/api/v1${path}`) : href.path(`/v6-6${path}`);
  }

  /**
   * Joy API 요청 주소를 생성한다. 인스턴스 생성 시 `baseURI`를 지정하지 않았으면 Sunny 서버를 가리킨다.
   */
  protected getAPIEndpoint(path: string): string {
    const baseURI = this.baseURI.href();
    return baseURI ? `${baseURI}/v6-6${path}` : `${baseURI}/api/v1${path}`;
  }

  /**
   * Joy API 요청 매개변수를 생성하고, 공통 헤더를 적용한다. 인스턴스 생성 시 `baseURI`를 지정하지 않았으면 Sunny 서버를 가리킨다. `this.getAPIEndpoint()`를 사용한다.
   * @param method
   * @param url API 엔드포인트의 경로. `resources`는 수동으로 적용해야 한다.
   * @param params `header`, `query`, `body` 객체.
   * @param overrides `timeout` 등, 재설정할 `axios` 옵션.
   * @param endpointBuilder `baseURI`와 `url`을 결합해 API 엔드포인트를 반환하는 함수. 기본값 `this.getAPIEndpoint`
   */
  protected makeConfig(
    method: THTTPMethods,
    url: string,
    params: IHTTPParams = {},
    overrides?: AxiosRequestConfig,
    endpointBuilder?: (path: string) => string, // 요청 5개 중 하나만 OpenAPI를 사용한다거나?
  ): IAxiosRequesterConfig {
    return {
      method,
      headers: this.makeHeaders(params?.headers),
      params: params?.query,
      data: params?.body,
      timeout: 5000,
      url: endpointBuilder ? endpointBuilder(url) : this.getAPIEndpoint(url),
      ...overrides,
    };
  }

  /**
   * 공통 헤더를 포함하여 요청 매개변수 유효성 검사를 수행한다.
   *
   * @see excuteValidation
   */
  protected validate<T extends Partial<IHTTPParams> | undefined>(
    schema: Record<string, unknown>,
    data: T,
    strict?: boolean,
  ): TValidateReturn<T> {
    if (
      checkIsBrowser() || // 클라이언트 사이드에선 공통 헤더가 없음
      !data // 요청 매개변수가 없으면 유효성 체크 안함
    ) {
      return excuteValidation(schema, data, strict);
    }
    const dataWithCommonHeaders = {
      ...data,
      headers: this.makeHeaders(data?.headers),
    };
    return excuteValidation(schema, dataWithCommonHeaders, strict);
  }
}
